import { DatePipe, TitleCasePipe } from '@angular/common'
import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { SeFeTranslationsLoader } from 'se-fe-translations'
import { CentralServiceResources as cs } from 'se-resource-types'

export const PERSONA_REQUIRED_FIELDS = [
  'first_name',
  'last_name',
  'date_of_birth',
  'gender',
  'graduation_year',
  'country',
  'phone',
  'email_address_1',
  'street_address_1',
  'street_address_2',
  'city',
  'state_province',
  'postal_code',
] as const
export type PersonaRequiredField = typeof PERSONA_REQUIRED_FIELDS[number]

// Default required fields
export const DEFAULT_PERSONA_REQUIRED_FIELDS = [
  'date_of_birth',
  'gender',
  'first_name',
  'last_name',
] as const satisfies readonly PersonaRequiredField[]

@Component({
  selector: 'se-po-persona-choice',
  templateUrl: './persona-choice.component.html',
  styleUrls: ['./persona-choice.component.scss'],
  standalone: false,
})
export class PersonaChoiceComponent implements OnInit {
  @Input() public profile: cs.Households.UserProfile
  @Input() public requiredFields?: PersonaRequiredField[]
  @Input() public profileFrame?: cs.Households.UserProfileFrame
  @Output() public edit = new EventEmitter<cs.Households.UserProfile>()
  @Output() public chosen = new EventEmitter<cs.Households.UserProfile>()
  @Output() public action = new EventEmitter<{ profile: cs.Households.UserProfile; action: { name: string } }>()
  @Output() public editMissingInfo = new EventEmitter<cs.Households.UserProfile>()

  public imageUrl: string
  public ready = false
  public subtitle: string[]
  public missingInfo = false

  constructor(
    private el: ElementRef,
    private seFeTranslationsLoader: SeFeTranslationsLoader,
    private datePipe: DatePipe,
    private titleCasePipe: TitleCasePipe,
    public translateService: TranslateService
  ) {}

  public async ngOnInit(): Promise<void> {
    // Inputs are typically set after construction; validate again to be safe
    this.validateRequiredFields(this.requiredFields)
    await this.initTranslations().then(() => {
      this.setFields()
    })
  }

  public handleHeaderClick($event): void {
    if(this.missingInfo) {
      this.onEditMissingInfo($event)
    } else {
      this.onChosen()
    }
  }

  public onEdit(e: MouseEvent): void {
    e.stopImmediatePropagation()
    this.edit.emit(this.profile)
  }

  public onEditMissingInfo(e: MouseEvent): void {
    this.editMissingInfo.emit(this.profile)
  }

  public onChosen(): void {
    this.chosen.emit(this.profile)
  }

  public onAction(action: { name: string }): void {
    this.action.emit({ profile: this.profile, action })
  }

  public onKeydown(e: KeyboardEvent): void {
    if (e.code === 'Space' || e.code === 'Enter') {
      this.onChosen()
    }
  }

  private async initTranslations(): Promise<void> {
    await this.seFeTranslationsLoader.load('shared/ui-persona-choice/assets/translations')
  }

  private setFields(): void {
    // Use provided requiredFields or default to DEFAULT_PERSONA_REQUIRED_FIELDS
    const fieldsToValidate = this.requiredFields || DEFAULT_PERSONA_REQUIRED_FIELDS

    // Check if any required field is missing
    const hasMissingFields = fieldsToValidate.some(field => !this.getProfileFieldValue(field))

    if (hasMissingFields) {
      this.missingInfo = true
      this.subtitle = this.translateService.instant('SE_PO_UI_PERSONA_CHOICE.MISSING_INFO')
    } else {
      const dob = this.profile?.persona?.date_of_birth ? this.datePipe.transform(this.profile.persona.date_of_birth, 'MMM d, y') : ''
      const genderKey = this.profile?.persona?.gender ? this.profile.persona.gender.toUpperCase() : ''
      const gender =
        genderKey ? this.translateService.instant('SE_PO_UI_PERSONA_CHOICE.' + genderKey) : ''
      this.subtitle = [dob, gender]
      this.missingInfo = false
    }
    this.setImageUrl()
    this.ready = true
  }

  private getProfileFieldValue(field: PersonaRequiredField): any {
    const persona = this.profile?.persona
    if (!persona) return null

    // Map field names to actual persona properties
    switch(field) {
      case 'first_name':
      case 'last_name':
      case 'date_of_birth':
      case 'gender':
      case 'graduation_year':
        return persona[field]
      case 'country':
      case 'city':
        return persona.address?.[field]
      case 'street_address_1':
        // Central service uses `street_1` while this component's API uses `street_address_1`
        return persona.address?.street_1 ?? (persona.address as any)?.['street_address_1']
      case 'street_address_2':
        return persona.address?.street_2 ?? (persona.address as any)?.['street_address_2']
      case 'phone':
        return persona.phone_numbers?.[0]?.number
      case 'email_address_1':
        return persona.email_addresses?.[0]?.address
      case 'state_province': // <- why is this different?
        return persona.address?.state_or_province
      case 'postal_code': // < - why is this different?
        return persona.address?.zip
      default:
        return null
    }
  }

  private setImageUrl(): void {
    const profileImages = this.profile?.persona?.profile_images
    this.imageUrl = (profileImages || []).find(i => i.image_type === 'crop')?.url
  }

  // Validates that every provided required field exists in PERSONA_REQUIRED_FIELDS
  private validateRequiredFields(fields?: readonly string[]): void {
    if (!fields || !Array.isArray(fields)) return
    const allowed = new Set(PERSONA_REQUIRED_FIELDS)
    const invalid = fields.filter(f => !allowed.has(f as any))
    if (invalid.length) {
      throw new Error(`Invalid requiredFields: ${invalid.join(', ')}. Allowed values: ${PERSONA_REQUIRED_FIELDS.join(', ')}`)
    }
  }
}
